import { ControlledAdressDadataAutocomplete } from '@sravni/cosago-react-library/lib/components';
import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { IFieldFactoryProps } from '@sravni/cosago-react-library/lib/types';
import { useCallback, useState } from 'react';

import { useFetchSuggestions } from 'shared/lib/useFetchSuggestions';

type TCommonAddressField<T extends Record<string, unknown>> = IFieldFactoryProps & {
  disabled?: boolean;
  handleOnHide: (newValue: Nullable<Dictionaries.TSelectValue>) => boolean;
  getAddress: (search: string) => Promise<Array<Dictionaries.TSelectOption<string, T>>>;
  onInitialSuggestsLoad?: (option: Dictionaries.TSelectOption<string, T>) => void;
  initialValue?: Nullable<Dictionaries.TSelectValue<string, T>>;
};

export const CommonAddressField = <T extends Record<string, unknown>>(props: TCommonAddressField<T>) => {
  const { type, label, getAddress, handleOnHide, onInitialSuggestsLoad, initialValue, isDisabled, ...restProps } =
    props;

  const [isInitialSuggestsLoaded, setIsInitialSuggestsLoaded] = useState(false);

  const getAddressSuggests = useCallback(
    async (value: string) => {
      const suggestions = await getAddress(value);

      if (isInitialSuggestsLoaded) {
        return suggestions;
      }

      setIsInitialSuggestsLoaded(true);

      const initialSuggest = initialValue
        ? suggestions.find((suggest) => {
            const { fias_id, city_fias_id } = initialValue.data || ({} as T);
            const { fias_id: suggestFiasId, city_fias_id: suggestCityFiasId } = suggest.data || ({} as T);

            return fias_id ? fias_id === suggestFiasId : city_fias_id === suggestCityFiasId;
          })
        : undefined;

      if (initialValue && suggestions.length && onInitialSuggestsLoad && initialSuggest) {
        onInitialSuggestsLoad(initialSuggest);
      }

      return suggestions;
    },
    [initialValue, isInitialSuggestsLoaded, getAddress, onInitialSuggestsLoad],
  );

  const { setSearchValue, searchValue, isLoading, currentSuggestions } = useFetchSuggestions(
    getAddressSuggests,
    String(initialValue?.value || ''),
    true,
  );

  const { trigger } = useFormContext();

  return (
    <ControlledAdressDadataAutocomplete
      {...restProps}
      isClearable
      suggestions={currentSuggestions}
      onSearch={setSearchValue}
      name={type}
      setDefaultSearchValue={setSearchValue}
      currentSearchValue={searchValue}
      isLoading={isLoading}
      label={label || ''}
      shouldHideSuggestionsList={handleOnHide}
      validate={() => trigger(type)}
      disabled={isDisabled}
    />
  );
};
